import {
  Kelas,
  Siswa,
  TahunAjaran,
  AkunKeuangan,
  PosTagihan,
  TagihanSiswa,
  TransaksiPembayaran,
  TabunganSiswa,
  ActivityLog,
  UserPrivilege,
  SettingTahunAjaran,
  PaymentGatewayConfig,
  SppGradeRule,
} from '../types/index.js';

const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const API_BASE = (typeof window !== 'undefined') ? '/api' : 'https://payment.wa-api.my.id/api';

/**
 * Ambil Authorization header dengan JWT token dari localStorage.
 */
function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const token = typeof window !== 'undefined'
    ? (localStorage.getItem('token') || localStorage.getItem('sdmt_token') || localStorage.getItem('portal_token'))
    : null;
  return {
    'Content-Type': 'application/json',
    ...(token ? {
      'Authorization': `Bearer ${token}`,
      'X-Authorization': `Bearer ${token}`,
      'X-Access-Token': token,
    } : {}),
    ...extra,
  };
}

/**
 * Wrapper fetch dengan Authorization header otomatis.
 * Jika server merespons 401, hapus token dan redirect ke login.
 */
async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const res = await fetch(url, {
    ...options,
    headers: {
      ...authHeaders(),
      ...(options.headers as Record<string, string> || {}),
    },
  });
  if (res.status === 401 && !url.includes('/auth/login') && !url.includes('/auth/portal/login')) {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('token');
      localStorage.removeItem('sdmt_token');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('isAuthenticated');
      window.dispatchEvent(new Event('auth-unauthorized'));
    }
  }
  return res;
}


export const api = {
  // === AUTH ===
  async login(credentials: { username: string; password: string; captcha?: string }) {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
    });
    const contentType = res.headers.get('content-type') || '';
    if (!res.ok) {
      if (contentType.includes('application/json')) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.message || `Login gagal (${res.status})`);
      } else {
        throw new Error(
          `Server backend tidak dapat dihubungi (${res.status} ${res.statusText || 'Service Unavailable'}). ` +
          `Pastikan layanan backend Express sedang berjalan.`
        );
      }
    }
    return res.json();
  },

  async getProfile() {
    const res = await apiFetch(`${API_BASE}/auth/profile`);
    return res.json();
  },

  // === ACADEMIC ===
  async getKelas(): Promise<Kelas[]> {
    const res = await apiFetch(`${API_BASE}/academic/kelas`);
    const json = await res.json();
    return json.data || [];
  },

  async createKelas(data: Partial<Kelas>): Promise<Kelas> {
    const res = await apiFetch(`${API_BASE}/academic/kelas`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    return json.data;
  },

  async updateKelas(id: string, data: Partial<Kelas>): Promise<Kelas> {
    const res = await apiFetch(`${API_BASE}/academic/kelas/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    return json.data;
  },

  async deleteKelas(id: string) {
    const res = await apiFetch(`${API_BASE}/academic/kelas/${id}`, { method: 'DELETE' });
    return res.json();
  },

  // === STUDENTS ===
  async getSiswa(params?: { kelasId?: string; search?: string }): Promise<Siswa[]> {
    const query = new URLSearchParams();
    if (params?.kelasId) query.append('kelasId', params.kelasId);
    if (params?.search) query.append('search', params.search);
    const res = await apiFetch(`${API_BASE}/students?${query.toString()}`);
    const json = await res.json();
    return (json.data || []).map((s: Siswa) => ({
      ...s,
      saldoEWallet: s.saldoEWallet ?? Math.floor((s.saldoTabungan || 0) * 0.3)
    }));
  },

  async getSiswaPaginated(params?: { kelasId?: string; search?: string; page?: number; limit?: number }): Promise<{ success: boolean; data: Siswa[]; totalCount: number; totalPages: number; page: number }> {
    const query = new URLSearchParams();
    if (params?.kelasId) query.append('kelasId', params.kelasId);
    if (params?.search) query.append('search', params.search);
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    const res = await apiFetch(`${API_BASE}/students?${query.toString()}`);
    const json = await res.json();
    if (json.data) {
      json.data = json.data.map((s: Siswa) => ({
        ...s,
        saldoEWallet: s.saldoEWallet ?? Math.floor((s.saldoTabungan || 0) * 0.3)
      }));
    }
    return json;
  },

  async getSiswaByNis(nis: string): Promise<Siswa> {
    const res = await apiFetch(`${API_BASE}/students/${nis}`);
    const json = await res.json();
    return json.data;
  },

  async createSiswa(data: Partial<Siswa>): Promise<Siswa> {
    const res = await apiFetch(`${API_BASE}/students`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    return json.data;
  },

  async updateSiswa(id: string, data: Partial<Siswa>): Promise<Siswa> {
    const res = await apiFetch(`${API_BASE}/students/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    return json.data;
  },

  async resetUserApk(id: string) {
    const res = await apiFetch(`${API_BASE}/students/${id}/reset-pin`, { method: 'POST' });
    return res.json();
  },

  async importSiswaExcel(payload: {
    students: any[];
    options?: { updateExisting?: boolean; autoGenerateVA?: boolean };
  }): Promise<{
    success: boolean;
    message: string;
    data: {
      total: number;
      inserted: number;
      updated: number;
      skipped: number;
      errors: Array<{ row: number; nis?: string; nama?: string; message: string }>;
    };
  }> {
    const res = await apiFetch(`${API_BASE}/students/import-excel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const json = await res.json();
    if (!res.ok) {
      throw new Error(json.message || 'Gagal mengimpor data siswa');
    }
    return json;
  },

  async verifyStudentPin(siswaId: string, pin: string): Promise<{ success: boolean; message: string; data?: any }> {
    const res = await apiFetch(`${API_BASE}/students/${siswaId}/verify-pin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin }),
    });
    return res.json();
  },

  async syncRfidAttendance(options?: { deviceIp?: string; autoUpdateLowAttendance?: boolean }) {
    const res = await apiFetch(`${API_BASE}/master/siswa/rfid-sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options || {}),
    });
    return res.json();
  },

  async updateStudentAttendance(nis: string, data: any) {
    const res = await apiFetch(`${API_BASE}/master/siswa/${nis}/attendance`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  },

  async getRfidLogs() {
    const res = await apiFetch(`${API_BASE}/master/rfid-logs`);
    return res.json();
  },

  async getBeasiswa(): Promise<any[]> {
    const res = await apiFetch(`${API_BASE}/billing/beasiswa`);
    const json = await res.json();
    return json.data || [];
  },

  async getJurnal(filters?: { startDate?: string; endDate?: string; keyword?: string; page?: number; limit?: number; akunId?: string; tipeReferensi?: string }): Promise<{ data: any[]; summary: any; totalPages?: number }> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.set('startDate', filters.startDate);
    if (filters?.endDate) params.set('endDate', filters.endDate);
    if (filters?.akunId) params.set('akunId', filters.akunId);
    if (filters?.tipeReferensi) params.set('tipeReferensi', filters.tipeReferensi);
    if (filters?.keyword) params.set('keyword', filters.keyword);
    if (filters?.page) params.set('page', filters.page.toString());
    if (filters?.limit) params.set('limit', filters.limit.toString());
    
    const res = await apiFetch(`${API_BASE}/accounting/jurnal?${params.toString()}`);
    const json = await res.json();
    return { data: json.data || [], summary: json.summary || { totalDebit: 0, totalKredit: 0 }, totalPages: json.totalPages || 1 };
  },
  async createBeasiswa(payload: any): Promise<any> {
    const res = await apiFetch(`${API_BASE}/billing/beasiswa`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return json.data;
  },
  async verifyBeasiswa(id: string, payload: { isApproved: boolean, catatanVerifikasi: string }): Promise<any> {
    const res = await apiFetch(`${API_BASE}/billing/beasiswa/${id}/verifikasi`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return json.data;
  },
  async batchVerifyBeasiswa(payload: any): Promise<any> {
    const res = await apiFetch(`${API_BASE}/billing/beasiswa/batch-verifikasi`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    return await res.json();
  },
  async deleteBeasiswa(id: string): Promise<any> {
    const res = await apiFetch(`${API_BASE}/billing/beasiswa/${id}`, { method: 'DELETE' });
    return await res.json();
  },

  async getTahunAjaran(): Promise<TahunAjaran[]> {
    const res = await apiFetch(`${API_BASE}/academic/tahun-ajaran`);
    const json = await res.json();
    return json.data || [];
  },

  async createTahunAjaran(data: Partial<TahunAjaran>): Promise<TahunAjaran> {
    const res = await apiFetch(`${API_BASE}/academic/tahun-ajaran`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    return json.data;
  },

  async getAkun(): Promise<AkunKeuangan[]> {
    const res = await apiFetch(`${API_BASE}/core/master/akun`);
    const json = await res.json();
    return json.data || [];
  },

  async updateAkun(id: string, data: Partial<AkunKeuangan>): Promise<AkunKeuangan> {
    const res = await apiFetch(`${API_BASE}/core/master/akun/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    return json.data;
  },
  async deleteAkun(id: string): Promise<any> {
    const res = await apiFetch(`${API_BASE}/core/master/akun/${id}`, { method: 'DELETE' });
    return await res.json();
  },
  async createAkun(data: Partial<AkunKeuangan>): Promise<AkunKeuangan> {
    const res = await apiFetch(`${API_BASE}/core/master/akun`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    return json.data;
  },

  async getPosTagihan(): Promise<PosTagihan[]> {
    const res = await apiFetch(`${API_BASE}/billing/pos-tagihan`);
    const json = await res.json();
    return json.data || [];
  },

  async createPosTagihan(data: Partial<PosTagihan>): Promise<PosTagihan> {
    const res = await apiFetch(`${API_BASE}/billing/pos-tagihan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    return json.data;
  },

  async updatePosTagihan(id: string, data: Partial<PosTagihan>): Promise<PosTagihan> {
    const res = await apiFetch(`${API_BASE}/billing/pos-tagihan/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    return json.data;
  },

  async getSetting(): Promise<SettingTahunAjaran> {
    const res = await apiFetch(`${API_BASE}/core/master/setting`);
    const json = await res.json();
    return json.data;
  },

  async getSettingTahunAjaran(): Promise<SettingTahunAjaran> {
    return this.getSetting();
  },

  async updateSetting(data: Partial<SettingTahunAjaran>): Promise<SettingTahunAjaran> {
    const res = await apiFetch(`${API_BASE}/core/master/setting`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    return json.data;
  },

  async updateSettingTahunAjaran(data: Partial<SettingTahunAjaran>): Promise<SettingTahunAjaran> {
    return this.updateSetting(data);
  },

  // === BILLING ===
  async getTagihanPaginated(params?: {
    siswaId?: string;
    nis?: string;
    kelasId?: string;
    status?: string;
    tahunAjaran?: string;
    posId?: string;
    page?: number;
    limit?: number;
  }): Promise<{ success: boolean; data: TagihanSiswa[]; totalCount: number; totalPages: number; page: number }> {
    const query = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v) query.append(k, v.toString());
      });
    }
    const res = await apiFetch(`${API_BASE}/billing/tagihan?${query.toString()}`);
    return res.json();
  },

  async getTagihan(params?: {
    siswaId?: string;
    nis?: string;
    kelas?: string;
    status?: string;
    tahunAjaran?: string;
    posId?: string;
  }): Promise<{ success: boolean; totalCount: number; data: TagihanSiswa[] }> {
    const query = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v) query.append(k, v);
      });
    }
    const res = await apiFetch(`${API_BASE}/billing/tagihan?${query.toString()}`);
    return res.json();
  },

  async getAllTagihan(): Promise<TagihanSiswa[]> {
    const res = await this.getTagihan();
    return res.data || [];
  },

  async getSppRules(): Promise<SppGradeRule[]> {
    const res = await apiFetch(`${API_BASE}/billing/spp-rules`);
    const json = await res.json();
    return json.data || [];
  },

  async createSppRule(data: Partial<SppGradeRule>): Promise<any> {
    const res = await apiFetch(`${API_BASE}/billing/spp-rules`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  },

  async updateSppRule(id: string, data: Partial<SppGradeRule>): Promise<any> {
    const res = await apiFetch(`${API_BASE}/billing/spp-rules/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  },

  async generateRecurringSpp(payload: {
    tahunAjaran: string;
    targetFilter?: 'ALL' | 'TINGKAT' | 'KELAS' | 'KATEGORI';
    targetValue?: string | number;
    posId?: string;
    startMonth?: string;
    endMonth?: string;
    overwriteExisting?: boolean;
  }) {
    const res = await apiFetch(`${API_BASE}/billing/generate-recurring-spp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.json();
  },

  async generateTagihan(payload: {
    targetType: 'SISWA' | 'KELAS' | 'ANGKATAN';
    targetId: string;
    posId: string;
    tahunAjaran: string;
    nominal: number;
    potongan?: number;
    mulaiBulan?: string;
  }) {
    const res = await apiFetch(`${API_BASE}/billing/tagihan/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.json();
  },

  async deleteTagihan(id: string) {
    const res = await apiFetch(`${API_BASE}/billing/tagihan/${id}`, { method: 'DELETE' });
    return res.json();
  },

  // === PAYMENT ===
  async processPayment(payload: {
    siswaId: string;
    tagihanIds?: string[];
    items?: { tagihanId: string; nominal: number }[];
    metodeBayar?: string;
    channel?: string;
    nominalBayar?: number;
    catatan?: string;
    keterangan?: string;
    kasir?: string;
    subMetode?: string;
  }): Promise<TransaksiPembayaran> {
    const formattedPayload = {
      siswaId: payload.siswaId,
      tagihanIds: payload.tagihanIds || payload.items?.map((i) => i.tagihanId) || [],
      items: payload.items,
      totalBayar: payload.nominalBayar || (payload.items ? payload.items.reduce((s, i) => s + i.nominal, 0) : 0),
      metodeBayar: payload.metodeBayar || payload.channel || 'MANUAL_CASH',
      channel: payload.channel || 'Mobile App',
      catatan: payload.catatan || payload.keterangan || '',
      kasir: payload.kasir || (typeof window !== 'undefined' ? (JSON.parse(localStorage.getItem('currentUser') || '{}')?.nama || JSON.parse(localStorage.getItem('currentUser') || '{}')?.username) : undefined) || 'Petugas Loket',
      subMetode: payload.subMetode,
    };

    const res = await apiFetch(`${API_BASE}/payment/process`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formattedPayload),
    });
    const json = await res.json();
    return json.transaction;
  },

  async generateQrisPayment(payload: {
    siswaId: string;
    tagihanIds: string[];
    nominalBayar?: number;
    kasir?: string;
  }) {
    const res = await apiFetch(`${API_BASE}/payment/qris/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.json();
  },

  async simulateQrisPayment(payload: {
    transactionId?: string;
    orderId?: string;
    simulatedIssuer?: string;
  }): Promise<{ success: boolean; message: string; transaction: TransaksiPembayaran }> {
    const res = await apiFetch(`${API_BASE}/payment/qris/simulate-pay`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.json();
  },

  async checkQrisStatus(orderId: string): Promise<{ success: boolean; status: string; transaction: TransaksiPembayaran }> {
    const res = await apiFetch(`${API_BASE}/payment/qris/status/${orderId}`);
    return res.json();
  },

  // Alias for VA polling (reuses same status endpoint)
  async checkPaymentStatus(noTransaksi: string): Promise<{ success: boolean; status: string; transaction: TransaksiPembayaran }> {
    const res = await apiFetch(`${API_BASE}/payment/qris/status/${noTransaksi}`);
    return res.json();
  },

  async generateVAPayment(payload: {
    siswaId: string;
    tagihanIds: string[];
    nominalBayar: number;
    bankCode: string; // BC=BCA, M2=Mandiri, BT=BNI, B1=BRI, I1=Muamalat
    kasir?: string;
  }): Promise<TransaksiPembayaran & { vaNumber?: string; expiredAt?: string; bankCode?: string }> {
    const res = await apiFetch(`${API_BASE}/payment/process`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        siswaId: payload.siswaId,
        tagihanIds: payload.tagihanIds,
        totalBayar: payload.nominalBayar,
        metodeBayar: `DUITKU_${payload.bankCode}`,
        channel: `VA_${payload.bankCode}`,
        kasir: payload.kasir || (typeof window !== 'undefined' ? (JSON.parse(localStorage.getItem('currentUser') || '{}')?.nama || JSON.parse(localStorage.getItem('currentUser') || '{}')?.username) : undefined) || 'Petugas Loket',
        subMetode: payload.bankCode,
      }),
    });
    const json = await res.json();
    return json.transaction;
  },

  async getTransaksiPaginated(params?: {
    nis?: string;
    siswaId?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<{ success: boolean; data: TransaksiPembayaran[]; totalCount: number; totalPages: number; page: number }> {
    const query = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v) query.append(k, v.toString());
      });
    }
    const res = await apiFetch(`${API_BASE}/payment?${query.toString()}`);
    return res.json();
  },

  async getTransactions(params?: {
    nis?: string;
    siswaId?: string;
    status?: string;
    kasir?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{ success: boolean; total: number; data: TransaksiPembayaran[] }> {
    const query = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v) query.append(k, v);
      });
    }
    const res = await apiFetch(`${API_BASE}/payment?${query.toString()}`);
    return res.json();
  },

  async getRekapKasir(params?: {
    startDate?: string;
    endDate?: string;
    kasir?: string;
  }): Promise<{
    success: boolean;
    data: {
      kasir: string;
      totalTransaksi: number;
      totalNominal: number;
      totalTunai: number;
      totalNonTunai: number;
      transaksiPertama: string;
      transaksiTerakhir: string;
    }[];
  }> {
    const query = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v) query.append(k, v);
      });
    }
    const res = await apiFetch(`${API_BASE}/payment/rekap-kasir?${query.toString()}`);
    return res.json();
  },

  async getTransaksi(): Promise<TransaksiPembayaran[]> {
    const res = await this.getTransactions();
    return res.data || [];
  },

  async cancelPayment(id: string, alasan: string, petugas?: string) {
    const res = await apiFetch(`${API_BASE}/payment/transactions/${id}/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ alasan, petugas: petugas || 'Admin Keuangan' }),
    });
    return res.json();
  },

  async cancelTransaksi(id: string, alasan: string) {
    return this.cancelPayment(id, alasan);
  },

  // === SAVINGS ===
  async getSaldoSiswa(params?: { kelasId?: string; search?: string }) {
    const query = new URLSearchParams();
    if (params?.kelasId) query.append('kelasId', params.kelasId);
    if (params?.search) query.append('search', params.search);
    const res = await apiFetch(`${API_BASE}/savings/saldo?${query.toString()}`);
    return res.json();
  },

  async getMutasiTabungan(params?: { siswaId?: string; nis?: string }): Promise<TabunganSiswa[]> {
    const query = new URLSearchParams();
    if (params?.siswaId) query.append('siswaId', params.siswaId);
    if (params?.nis) query.append('nis', params.nis);
    const res = await apiFetch(`${API_BASE}/savings/mutasi?${query.toString()}`);
    const json = await res.json();
    return json.data || [];
  },

  async cancelMutasiTabungan(id: string): Promise<{ success: boolean; message: string; data?: any }> {
    const res = await apiFetch(`${API_BASE}/savings/mutasi/${id}`, {
      method: 'DELETE',
    });
    return res.json();
  },

  async depositTabungan(payload: { siswaId: string; nominal: number; keterangan?: string; petugas?: string; sumberDana?: string; channel?: string }) {
    const res = await apiFetch(`${API_BASE}/savings/mutasi`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, jenis: 'SETOR' }),
    });
    return res.json();
  },

  async withdrawTabungan(payload: { siswaId: string; nominal: number; keterangan?: string; petugas?: string }) {
    const res = await apiFetch(`${API_BASE}/savings/mutasi`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, jenis: 'TARIK' }),
    });
    return res.json();
  },

  async createMutasiTabungan(payload: { siswaId: string; tipe: 'SETOR' | 'TARIK'; nominal: number; keterangan?: string; sumberDana?: string; channel?: string }) {
    if (payload.tipe === 'SETOR') {
      return this.depositTabungan(payload);
    } else {
      return this.withdrawTabungan(payload);
    }
  },

  async topUpTabungan(payload: { siswaId: string; nominal: number; channel: string; subMetode?: string }) {
    const res = await apiFetch(`${API_BASE}/savings/topup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.json();
  },

  async topUpTabunganFromEWallet(payload: { siswaId: string; nominal: number; keterangan?: string }) {
    return this.topUpTabungan({
      siswaId: payload.siswaId,
      nominal: payload.nominal,
      channel: 'EWALLET',
    });
  },

  async getMutasiEWallet(params?: { siswaId?: string; nis?: string }) {
    const query = new URLSearchParams();
    if (params?.siswaId) query.append('siswaId', params.siswaId);
    if (params?.nis) query.append('nis', params.nis);
    const res = await apiFetch(`${API_BASE}/ewallet/mutasi?${query.toString()}`);
    const json = await res.json();
    return json.data || [];
  },

  async topUpEWallet(payload: { siswaId: string; nominal: number; channel: string; subMetode?: string }) {
    const res = await apiFetch(`${API_BASE}/ewallet/topup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.json();
  },

  async mutateEWallet(payload: { siswaId: string; nominal: number; jenis: 'SETOR' | 'TARIK' | 'BELANJA'; keterangan?: string }) {
    const res = await apiFetch(`${API_BASE}/ewallet/mutate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.json();
  },

  async getCetakBukuTabungan(siswaId: string) {
    const res = await apiFetch(`${API_BASE}/savings/siswa/${siswaId}?order=asc`);
    const json = await res.json();
    return { success: json.success, transaksi: json.data || [], halaman_aktif: 1, baris_aktif: 1 };
  },

  async logCetakBuku(siswaId: string, payload: { id_transaksi: string; halaman_ke: number; baris_ke: number; tipe_cetak: string; petugas: string }) {
    const res = await apiFetch(`${API_BASE}/savings/cetak/${siswaId}/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.json();
  },

  // === REPORTS ===
  async getDashboardStats() {
    const res = await apiFetch(`${API_BASE}/core/reports/dashboard-stats`);
    const json = await res.json();
    return json.data;
  },

  async getReportTagihan(params?: Record<string, string>) {
    const query = new URLSearchParams(params);
    const res = await apiFetch(`${API_BASE}/reports/tagihan?${query.toString()}`);
    return res.json();
  },

  async getReportPembayaran(params?: Record<string, string>) {
    const query = new URLSearchParams(params);
    const res = await apiFetch(`${API_BASE}/reports/pembayaran?${query.toString()}`);
    return res.json();
  },

  async getReportTunggakan(params?: Record<string, string>) {
    const query = new URLSearchParams(params);
    const res = await apiFetch(`${API_BASE}/reports/tunggakan?${query.toString()}`);
    return res.json();
  },

  async getReportPenerimaan() {
    const res = await apiFetch(`${API_BASE}/reports/penerimaan`);
    return res.json();
  },

  // === UTILITY ===
  async getActivityLogs(params?: Record<string, string>): Promise<ActivityLog[]> {
    const query = new URLSearchParams(params);
    const res = await apiFetch(`${API_BASE}/utility/logs?${query.toString()}`);
    const json = await res.json();
    return json.data || [];
  },

  async getUserPrivileges(): Promise<UserPrivilege[]> {
    const res = await apiFetch(`${API_BASE}/utility/privileges`);
    const json = await res.json();
    return json.data || [];
  },

  async createUserPrivilege(data: Partial<UserPrivilege>) {
    const res = await apiFetch(`${API_BASE}/utility/privileges`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) {
      throw new Error(json.message || `Gagal menambahkan pengguna (${res.status})`);
    }
    return json;
  },

  async updateUserPrivilege(id: string, data: Partial<UserPrivilege>) {
    const res = await apiFetch(`${API_BASE}/utility/privileges/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) {
      throw new Error(json.message || `Gagal memperbarui pengguna (${res.status})`);
    }
    return json;
  },

  async deleteUserPrivilege(id: string) {
    const res = await apiFetch(`${API_BASE}/utility/privileges/${id}`, {
      method: 'DELETE',
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) {
      throw new Error(json.message || `Gagal menghapus pengguna (${res.status})`);
    }
    return json;
  },

  async promoteClass(fromKelasId: string, toKelasId: string, studentIds: string[] = []) {
    const res = await apiFetch(`${API_BASE}/utility/naik-kelas`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fromKelasId, toKelasId, studentIds }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) {
      throw new Error(json.message || `Gagal memproses kenaikan kelas (${res.status})`);
    }
    return json;
  },

  async graduateClass(kelasId: string, studentIds: string[] = []) {
    const res = await apiFetch(`${API_BASE}/utility/kelulusan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kelasId, studentIds }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) {
      throw new Error(json.message || `Gagal memproses kelulusan (${res.status})`);
    }
    return json;
  },

  async downloadBackup() {
    const res = await apiFetch(`${API_BASE}/utility/backup`);
    if (!res.ok) {
      throw new Error(`Gagal mendownload backup database (${res.status})`);
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `backup_sdmt_${new Date().toISOString().slice(0, 10)}.sql`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  async restoreDatabase(formData: FormData) {
    const token = typeof window !== 'undefined'
      ? (localStorage.getItem('token') || localStorage.getItem('sdmt_token') || localStorage.getItem('portal_token'))
      : null;
    const res = await fetch(`${API_BASE}/utility/restore`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) {
      throw new Error(json.message || `Gagal memulihkan database (${res.status})`);
    }
    return json;
  },

  // === GATEWAYS ===
  async getGatewayConfig(): Promise<PaymentGatewayConfig> {
    const res = await apiFetch(`${API_BASE}/gateways/config`);
    const json = await res.json();
    return json.config;
  },

  async updateGatewayConfig(data: Partial<PaymentGatewayConfig>) {
    const res = await apiFetch(`${API_BASE}/gateways/config`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  },

  async simulateDuitkuWebhook(payload: {
    merchantOrderId: string;
    amount: number;
    resultCode: string;
    paymentCode?: string;
  }) {
    const res = await apiFetch(`${API_BASE}/gateways/duitku/callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        merchantOrderId: payload.merchantOrderId,
        amount: payload.amount.toString(),
        resultCode: payload.resultCode,
        paymentCode: payload.paymentCode || 'VA',
      }),
    });
    return res.json();
  },

  async simulateMidtransWebhook(payload: {
    order_id: string;
    gross_amount: string;
    transaction_status: string;
    payment_type?: string;
  }) {
    const res = await apiFetch(`${API_BASE}/gateways/midtrans/notification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.json();
  },

  // === WHATSAPP NOTIFICATIONS ===
  async getWhatsAppConfig() {
    const res = await apiFetch(`${API_BASE}/whatsapp/config`);
    return res.json();
  },

  async updateWhatsAppConfig(config: any) {
    const res = await apiFetch(`${API_BASE}/whatsapp/config`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    });
    return res.json();
  },

  async previewWhatsAppMessage(transactionId: string) {
    const res = await apiFetch(`${API_BASE}/whatsapp/preview/${transactionId}`);
    return res.json();
  },

  async sendWhatsAppReceipt(transactionId: string, customPhone?: string) {
    const res = await apiFetch(`${API_BASE}/whatsapp/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transactionId, customPhone }),
    });
    return res.json();
  },
};
